import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { FEATURE_NAMES, SYSTEM_FIELD_CONFIG } from "@/lib/plans/enforce";
import { Plan } from "@/lib/plans/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const editPlanSchema = z.object({
  name: z.string().min(1, "Nome é obrigatório"),
  description: z.string().optional(),
  price_monthly: z.number().min(0, "Preço mensal deve ser positivo"),
  price_yearly: z.number().min(0, "Preço anual deve ser positivo"),
  features: z.object({
    limits: z.record(z.number().optional()),
    toggles: z.object({}).optional(),
  }),
  is_active: z.boolean(),
});

type EditPlanData = z.infer<typeof editPlanSchema>;

interface EditPlanModalProps {
  open: boolean;
  onClose: () => void;
  plan: Plan | null;
  systemCode: string;
}

export function EditPlanModal({ open, onClose, plan, systemCode }: EditPlanModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();

  const updatePlanMutation = useMutation({
    mutationFn: async (data: EditPlanData) => {
      if (!plan) throw new Error('No plan to update');
      
      const { error } = await supabase
        .from('plans')
        .update({
          name: data.name,
          description: data.description,
          price_monthly: data.price_monthly,
          price_yearly: data.price_yearly,
          features: data.features,
          is_active: data.is_active,
          updated_at: new Date().toISOString()
        })
        .eq('id', plan.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-plans'] });
      toast.success('Plano atualizado com sucesso!');
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.message || 'Erro ao atualizar plano');
    },
  });

  const form = useForm<EditPlanData>({
    resolver: zodResolver(editPlanSchema),
    defaultValues: {
      name: "",
      description: "",
      price_monthly: 0,
      price_yearly: 0,
      features: {
        limits: {
          fleet_vehicles_max: 10,
        },
        toggles: {},
      },
      is_active: true,
    },
  });

  useEffect(() => {
    if (plan && open) {
      form.reset({
        name: plan.name,
        description: plan.description || "",
        price_monthly: plan.price_monthly,
        price_yearly: plan.price_yearly,
        features: plan.features,
        is_active: plan.is_active,
      });
    }
  }, [plan, open, form]);

  const onSubmit = async (data: EditPlanData) => {
    if (!plan) return;
    
    setIsSubmitting(true);
    try {
      await updatePlanMutation.mutateAsync(data);
    } catch (error) {
      console.error("Error updating plan:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Plano: {plan?.name}</DialogTitle>
          <DialogDescription>
            Modifique as configurações do plano
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome do Plano</FormLabel>
                    <FormControl>
                      <Input placeholder="Ex: Básico, Profissional, Enterprise" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Descrição</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Descrição do plano..." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="price_monthly"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Preço Mensal (R$)</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          placeholder="0.00" 
                          {...field}
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="price_yearly"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Preço Anual (R$)</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          placeholder="0.00" 
                          {...field}
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="is_active"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between p-3 border rounded-lg">
                    <FormLabel>Plano Ativo</FormLabel>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <Separator />

            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Limite de Veículos</h3>
              <FormField
                control={form.control}
                name="features.limits.fleet_vehicles_max"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Quantidade de Veículos</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        placeholder="Ex: 10, 50, 100"
                        {...field}
                        value={field.value || ''}
                        onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Salvando..." : "Salvar Alterações"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}